import Fingerprint2 from "fingerprintjs2";
import FingerprintJS from "@fingerprintjs/fingerprintjs-pro";

import { logger } from "@jslib/shared/utils/logger";

import { getOptions } from "../../options/options";

type FingerprintProperties = {
  clientId: string;
  workerEndpoint: string;
  scriptUrl: string;
};

/**
 * @description Gets Fingerprint config from window properties passed in from view-main template.
 *              This avoids env specific logic in jslib.
 */
const getFingerprintProperties = (): FingerprintProperties => {
  const { clientId, workerEndpoint, scriptUrl } = window.FP || {};
  if (
    !clientId ||
    !scriptUrl ||
    !scriptUrl.match(/^https:\/\/worker(-preview)?\.sheerid\.com\//) ||
    !workerEndpoint ||
    !workerEndpoint.match(/^https:\/\/worker(-preview)?\.sheerid\.com\//)
  ) {
    throw new Error("Fingerprint environment not set properly");
  }

  return { clientId, workerEndpoint, scriptUrl };
};

/**
 * @description The user's device fingerprint
 */
let fingerprint: string;

/**
 * @description Get the digital fingerprint hash for a user, based on various digital signals.
 *              Recommended: call this at least 500ms after page ready
 */
export const getFingerprint = async (): Promise<string> => {
  if (!getOptions().useFingerprinting) {
    return Promise.resolve(undefined);
  }

  if (fingerprint) {
    return Promise.resolve(fingerprint);
  }

  return Fingerprint2.getPromise({}).then((components) => {
    const values = components.map((component) => component.value);
    const murmur = Fingerprint2.x64hash128(values.join(""), 31);
    fingerprint = murmur;
    return fingerprint;
  });
};

let fpPromise: Promise<string> | null = null;

/**
 * @description Initializes Fingerprint device collection and returns a promise that resolves
 *              with Fingerprint's "Request ID".
 */
export const getFingerprintRequestId = (): Promise<string> => {
  if (fpPromise) {
    return fpPromise;
  }

  fpPromise = (async () => {
    try {
      const { clientId, workerEndpoint, scriptUrl } = getFingerprintProperties();

      const fp = await FingerprintJS.load({
        apiKey: clientId,
        endpoint: [workerEndpoint, FingerprintJS.defaultEndpoint],
        scriptUrlPattern: [
          `${scriptUrl}?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>`,
          FingerprintJS.defaultScriptUrlPattern,
        ],
      });

      const result = await fp.get();

      return result.requestId;
    } catch (error) {
      // Allow retries on failure
      fpPromise = null;
      logger.error(error, "Failed to load Fingerprint SDK");
      throw error;
    }
  })();

  return fpPromise;
};
